import * as z from 'zod';

/**
 * Offset paging. Query values arrive as strings, hence `coerce`; the client
 * sends numbers and the same schema accepts both.
 */
export const DEFAULT_PAGE_SIZE = 25;
export const MAX_PAGE_SIZE = 200;

export const PageQuerySchema = z.strictObject({
  page: z.coerce.number().int().min(1).max(100_000).optional(),
  pageSize: z.coerce.number().int().min(1).max(MAX_PAGE_SIZE).optional(),
});
export type PageQuery = z.infer<typeof PageQuerySchema>;

export const PageInfoSchema = z.strictObject({
  page: z.int().min(1),
  pageSize: z.int().min(1).max(MAX_PAGE_SIZE),
  total: z.int().min(0),
  totalPages: z.int().min(0),
  hasNext: z.boolean(),
});
export type PageInfo = z.infer<typeof PageInfoSchema>;

/** The one list envelope. Every paged response in the platform is this shape. */
export const paginated = <TItem extends z.ZodType>(item: TItem) =>
  z.strictObject({ items: z.array(item), page: PageInfoSchema });

export interface Paginated<TItem> {
  readonly items: readonly TItem[];
  readonly page: PageInfo;
}

/** Resolve a page query against the platform defaults. Shared so API and web agree. */
export const resolvePageQuery = (query: PageQuery): { page: number; pageSize: number } => ({
  page: query.page ?? 1,
  pageSize: query.pageSize ?? DEFAULT_PAGE_SIZE,
});

export const buildPageInfo = (
  query: { page: number; pageSize: number },
  total: number,
): PageInfo => {
  const totalPages = query.pageSize > 0 ? Math.ceil(total / query.pageSize) : 0;
  return {
    page: query.page,
    pageSize: query.pageSize,
    total,
    totalPages,
    hasNext: query.page < totalPages,
  };
};
